import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import { useSettingsApi } from '../../hooks/useSettingsApi';
import {
  OnboardingSlides,
  AppDesign,
  SocialLogin,
  GoogleMaps,
  SettingsResponse,
  // BannerForm,
  ConfigurationSettings,
} from '../../types/settings';
import { useEffect } from 'react';
import { useQueryParams } from '../HelperFunctions/Hooks/useQueryParams';

export const useSettingsQuery = () => {
  const { showMessage } = useSnackbar();
  const { createSettings } = useSettingsApi();

  return useQuery<SettingsResponse>({
    queryKey: ['settings'],
    queryFn: async () => {
      try {
        const response = await createSettings({});
        return response;
      } catch (error: any) {
        showMessage(
          error.response?.data?.message || 'Failed to fetch settings',
          'error'
        );
        throw error;
      }
    },
  });
};

export const useSettingsCreate = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { createSettings } = useSettingsApi();

  return useMutation<SettingsResponse, Error, Partial<ConfigurationSettings>>({
    mutationKey: ['createSettings'],
    mutationFn: (data: Partial<ConfigurationSettings>) => createSettings(data),
    onSuccess: (response) => {
      if (response?.message) {
        showMessage(response.message, 'success');
      } else {
        showMessage('Settings updated successfully', 'success');
      }
      queryClient.invalidateQueries({
        queryKey: ['settings'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to update settings',
        'error'
      );
    },
  });
};

export const useOnboardingSlidesUpdate = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { updateOnboardingSlides } = useSettingsApi();

  return useMutation<SettingsResponse, Error, OnboardingSlides>({
    mutationKey: ['updateOnboardingSlides'],
    mutationFn: (data: OnboardingSlides) => updateOnboardingSlides(data),
    onSuccess: (response) => {
      showMessage(
        response?.message || 'Onboarding slides updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['settings'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to update onboarding slides',
        'error'
      );
    },
  });
};

export const useAppDesignUpdate = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { updateAppDesign } = useSettingsApi();

  return useMutation<SettingsResponse, Error, AppDesign>({
    mutationKey: ['updateAppDesign'],
    mutationFn: (data: AppDesign) => updateAppDesign(data),
    onSuccess: (response) => {
      showMessage(
        response?.message || 'App design updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['settings'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to update app design',
        'error'
      );
    },
  });
};

export const useSocialLoginUpdate = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { updateSocialLogin } = useSettingsApi();

  return useMutation<SettingsResponse, Error, SocialLogin>({
    mutationKey: ['updateSocialLogin'],
    mutationFn: (data: SocialLogin) => updateSocialLogin(data),
    onSuccess: (response) => {
      showMessage(
        response?.message || 'Social login settings updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['settings'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message ||
          'Failed to update social login settings',
        'error'
      );
    },
  });
};

export const useGoogleMapsUpdate = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { updateGoogleMaps } = useSettingsApi();

  return useMutation<SettingsResponse, Error, GoogleMaps>({
    mutationKey: ['updateGoogleMaps'],
    mutationFn: (data: GoogleMaps) => updateGoogleMaps(data),
    onSuccess: (response) => {
      showMessage(
        response?.message || 'Google Maps settings updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['settings'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message ||
          'Failed to update Google Maps settings',
        'error'
      );
    },
  });
};

export const useGetSearch = ({
  isMainScreen = false,
  initialData,
}: {
  initialData?: any;
  isMainScreen?: boolean;
  key?: any;
}) => {
  const { query, queryhash, setFilters, filters, setQuery, updateQuery } =
    useQueryParams<any>({ isMainScreen, initialData });

  const { getAllSettings } = useSettingsApi();
  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['getSettings', query.language],
    queryFn: () => {
      return getAllSettings(query);
    },
  });
  useEffect(() => {
    if (queryhash) {
      refetch();
    }
  }, [queryhash]);

  const isLoading = isFetching || isRefetching;
  return { data, isLoading, query, setQuery, filters, setFilters, updateQuery };
};
